import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { charactersList } from '@/mocks/characters';
import { guidesList } from '@/mocks/guides';
import { useSeo } from '@/hooks/useSeo';
import { getElementStyle } from '@/utils/elements';
import { formatDate } from '@/utils/date';
import Breadcrumb from '@/components/base/Breadcrumb';
import FavoriteButton from '@/components/base/FavoriteButton';
import ShareModal from '@/components/feature/ShareModal';

const TALENT_ICONS = ['ri-sword-line', 'ri-flashlight-fill', 'ri-fire-fill', 'ri-star-smile-line'];

export default function CharacterDetail() {
  const { id } = useParams<{ id: string }>();
  const [shareOpen, setShareOpen] = useState(false);
  const character = useMemo(() => charactersList.find((item) => item.id === id), [id]);

  useSeo({
    title: character ? `角色攻略：${character.name} – 原神官方游戏站` : '角色攻略 – 原神官方游戏站',
    description: character
      ? `${character.name}是${character.elementName}元素${character.roleName}角色，本文提供其武器、圣遗物、技能天赋与配队培养建议。`
      : '查看原神角色攻略详情。',
    keywords: character ? `原神${character.name},${character.name}攻略,原神角色培养` : '原神角色攻略',
    canonicalPath: `/characters/${id}`,
  });

  if (!character) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-user-search-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到该角色</h1>
        <Link
          to="/characters"
          className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-arrow-left-line"></i>
          返回角色列表
        </Link>
      </div>
    );
  }

  const element = getElementStyle(character.element);
  const relatedGuides = guidesList.filter((guide) => guide.characterId === character.id);
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-800 via-primary-900 to-primary-950">
        <div className="absolute inset-0">
          <img src={character.splash} alt="" aria-hidden="true" className="h-full w-full object-cover object-top opacity-25" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14 lg:px-8">
          <div className="text-background-50/70">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '角色攻略', to: '/characters' }, { label: character.name }]} />
          </div>
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:items-center">
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-label ${element.badge}`}>
                  <i className={`${element.icon} text-sm`}></i>
                  {element.label}元素
                </span>
                <span className="inline-flex items-center gap-0.5 text-accent-300">
                  {Array.from({ length: character.rarity }).map((_, index) => (
                    <i key={`${character.id}-star-${index}`} className="ri-star-fill text-sm"></i>
                  ))}
                </span>
                <span className="rounded-full bg-background-50/10 px-3 py-1 text-xs font-label text-background-50">{character.roleName}</span>
              </div>
              <h1 className="mt-4 font-heading text-3xl font-black text-background-50 md:text-5xl">{character.name}</h1>
              <p className="mt-2 text-sm font-label text-accent-300 md:text-base">{character.title}</p>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-50/80 md:text-base">{character.intro}</p>

              <div className="mt-6 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-lg bg-background-50/10 px-3.5 py-2 text-xs text-background-50 backdrop-blur-sm">
                  <i className="ri-sword-line text-accent-300"></i>
                  武器：{character.weapon}
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg bg-background-50/10 px-3.5 py-2 text-xs text-background-50 backdrop-blur-sm">
                  <i className="ri-map-pin-line text-accent-300"></i>
                  地区：{character.region}
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg bg-background-50/10 px-3.5 py-2 text-xs text-background-50 backdrop-blur-sm">
                  <i className="ri-calendar-line text-accent-300"></i>
                  上线：{formatDate(character.releaseDate)}
                </span>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/search?q=${encodeURIComponent(character.name)}`}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label font-medium text-foreground-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-book-open-line"></i>
                  查看相关攻略
                </Link>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
            </div>

            <div className="hidden lg:col-span-1 lg:block">
              <div className="mx-auto h-[360px] w-[260px] overflow-hidden rounded-2xl border border-background-50/20">
                <img src={character.splash} alt={character.name} title={`${character.name} 立绘`} className="h-full w-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                <a href="#stats">角色属性介绍</a>
              </h2>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {Object.entries(character.stats).map(([key, value]) => (
                  <div key={key} className="rounded-xl border border-background-200 bg-background-50 p-4">
                    <p className="text-xs text-foreground-500">{key}</p>
                    <p className="mt-1.5 font-heading text-base font-bold text-foreground-950">{value}</p>
                  </div>
                ))}
                <div className="rounded-xl border border-accent-200 bg-accent-50 p-4">
                  <p className="text-xs text-accent-800">角色定位</p>
                  <p className="mt-1.5 font-heading text-base font-bold text-accent-900">{character.roleName}</p>
                </div>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                <a href="#talents">技能与天赋详细说明</a>
              </h2>
              <div className="mt-5 space-y-4">
                {character.talents.map((talent, index) => (
                  <div key={talent.name} className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${TALENT_ICONS[index % TALENT_ICONS.length]} text-xl`}></i>
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-heading text-base font-bold text-foreground-950">{talent.name}</h3>
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">{talent.type}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{talent.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                <a href="#play">玩法建议与搭配推荐</a>
              </h2>
              <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-label font-medium text-foreground-900">推荐配队</h3>
                <div className="mt-3 space-y-2.5">
                  {character.teamComp.map((team) => (
                    <div key={team} className="flex items-center gap-2 rounded-lg bg-primary-50 px-3.5 py-2.5 text-sm text-foreground-800">
                      <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                        <i className="ri-team-line"></i>
                      </span>
                      {team}
                    </div>
                  ))}
                </div>
                <h3 className="mt-6 text-sm font-label font-medium text-foreground-900">实战要点</h3>
                <ul className="mt-3 space-y-2.5">
                  {character.playTips.map((tip, index) => (
                    <li key={tip} className="flex items-start gap-3">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-[11px] font-label text-accent-800">
                        {index + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  <a href="#related">相关攻略</a>
                </h3>
                {relatedGuides.length > 0 ? (
                  <ul className="mt-3 space-y-3">
                    {relatedGuides.map((guide) => (
                      <li key={guide.id}>
                        <Link to={`/guides/${guide.id}`} className="group block cursor-pointer">
                          <p className="line-clamp-2 text-sm text-foreground-800 transition-colors group-hover:text-primary-700">{guide.title}</p>
                          <p className="mt-1 text-[11px] text-foreground-400">
                            {formatDate(guide.date)} · {guide.readTime}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-3">
                    <p className="text-sm text-foreground-500">暂无专门的攻略文章，可查看该角色的搜索结果。</p>
                    <Link
                      to={`/search?q=${encodeURIComponent(character.name)}`}
                      className="mt-3 inline-flex cursor-pointer items-center gap-1 text-sm text-primary-700 transition-colors hover:text-primary-600"
                    >
                      搜索相关内容
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <FavoriteButton id={`char-${character.id}`} variant="full" />
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">收藏该角色页，方便随时回来看养成与配队建议。</p>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={`${character.name} 角色攻略`} url={currentUrl} />
    </div>
  );
}